/**
 * DecisionFlow — CommercialRoleLibrary
 * ui/src/components/admin/CommercialRoleLibrary.jsx
 * S615
 *
 * Three-tab surface: Platform Library / My Roles / Selling Motions
 * Route: /admin/commercial-roles (already wired in CompAdminShell)
 *
 * Spec authority: crl_spec_v1_0.md §11
 * Backend: GET /api/v1/role-library, POST /api/v1/role-library/adopt,
 *          GET/POST/PATCH/DELETE /api/v1/roles,
 *          GET/POST/PATCH/DELETE /api/v1/selling-motions,
 *          GET/POST/DELETE /api/v1/role-motion
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind. Zero dark: variants.
 * No <form> tags. Seen-flag tracking via React useState only.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, Plus, Search, ChevronDown, ChevronRight,
  Lock, CheckCircle, Edit2, MoreHorizontal, X, Loader2,
  Zap, RefreshCw, Users, FileText,
} from "lucide-react";
import RoleLibraryPopup  from "./RoleLibraryPopup";
import RoleAdoptionModal from "./RoleAdoptionModal";
import RoleAssignmentWizard from "./RoleAssignmentWizard";

const API = "/api/v1";

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    const msg  = body?.detail?.error?.message ?? body?.detail ?? `HTTP ${r.status}`;
    throw Object.assign(new Error(msg), { code: body?.detail?.error?.code, status: r.status });
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// Motion chip
// ---------------------------------------------------------------------------
function MotionChip({ label }) {
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))]">
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
function Toast({ message, type = "success", onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  const colors = type === "error"
    ? "bg-[rgb(var(--color-danger))]/10 border-[rgb(var(--color-danger))] text-[rgb(var(--color-danger))]"
    : "bg-[rgb(var(--color-success))]/10 border-[rgb(var(--color-success))] text-[rgb(var(--color-success))]";
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl border px-4 py-3 shadow-lg text-tok-sm font-medium ${colors}`}>
      {message}
      <button type="button" onClick={onDismiss}><X className="h-4 w-4" /></button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline form for custom role creation (My Roles tab)
// ---------------------------------------------------------------------------
function AddCustomRoleForm({ onSave, onCancel }) {
  const [fields, setFields] = useState({ role_code: "", role_name: "", role_category: "ic", role_description: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setErr(null);
    try {
      const data = await apiFetch("/roles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...fields, role_code: fields.role_code.toUpperCase().trim() }),
      });
      onSave(data);
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 mb-3">
      <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))] mb-3">New Custom Role</p>
      {err && <p className="text-tok-xs text-[rgb(var(--color-danger))] mb-2">{err}</p>}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Role Code *</label>
          <input
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
            placeholder="AE_ENT"
            value={fields.role_code}
            onChange={e => set("role_code", e.target.value)}
            maxLength={20}
          />
        </div>
        <div>
          <label className="block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Role Name *</label>
          <input
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
            placeholder="Enterprise Account Executive"
            value={fields.role_name}
            onChange={e => set("role_name", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Category</label>
          <select
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            value={fields.role_category}
            onChange={e => set("role_category", e.target.value)}
          >
            <option value="ic">Individual Contributor (ic)</option>
            <option value="rollup">Rollup Manager (rollup)</option>
            <option value="hybrid">Hybrid / Player-Coach (hybrid)</option>
          </select>
        </div>
        <div>
          <label className="block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Description</label>
          <input
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            placeholder="Optional"
            value={fields.role_description}
            onChange={e => set("role_description", e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">Cancel</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !fields.role_code.trim() || !fields.role_name.trim()}
          className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Role"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Platform Library Tab
// ---------------------------------------------------------------------------
function PlatformLibraryTab({ libraryRoles, motions, onAdopt, loading }) {
  const [search, setSearch]       = useState("");
  const [popupRole, setPopupRole] = useState(null);

  const filtered = libraryRoles.filter(r =>
    !search || r.display_name.toLowerCase().includes(search.toLowerCase()) ||
    r.archetype.toLowerCase().includes(search.toLowerCase())
  );

  // Group by role_category
  const groups = { ic: [], rollup: [], hybrid: [] };
  filtered.forEach(r => { (groups[r.role_category] ?? []).push(r); });

  const GROUP_LABELS = {
    ic:      "Individual Contributor",
    rollup:  "Management — Rollup",
    hybrid:  "Management — Hybrid / Player-Coach",
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Platform Library</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Browse data-fit curated commercial role archetypes and add them to your tenant.</p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
          <input
            className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pl-8 pr-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] w-52"
            placeholder="Search archetypes…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[rgb(var(--color-text-muted))]" />
        </div>
      )}

      {!loading && Object.entries(groups).map(([cat, roles]) => {
        if (!roles.length) return null;
        return (
          <div key={cat} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <ChevronDown className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                {GROUP_LABELS[cat]}
              </span>
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">— {roles.length} archetypes</span>
            </div>

            <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden">
              <table className="w-full border-collapse">
                <thead className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                  <tr>
                    <th className="w-8 px-4 py-2"></th>
                    <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Role Name</th>
                    <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Selling Motions</th>
                    <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Archetype</th>
                    <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Industries</th>
                    <th className="w-36 px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map(role => {
                    const roleMotive = motions.find(m => m.motion_code === role.primary_motion_code);
                    // industry_tags arrives as JSON string from DB jsonb — parse defensively
                    let tags = [];
                    if (Array.isArray(role.industry_tags)) {
                      tags = role.industry_tags;
                    } else if (typeof role.industry_tags === "string") {
                      try { tags = JSON.parse(role.industry_tags); } catch { tags = []; }
                    }
                    return (
                      <tr
                        key={role.ref_role_id}
                        onClick={() => setPopupRole(role)}
                        className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]/40 cursor-pointer"
                      >
                        {/* Checkbox — uncontrolled; bulk adopt is post-GA scope */}
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            disabled={role.is_adopted}
                            aria-label={`Select ${role.display_name}`}
                            className="h-4 w-4 appearance-none rounded border-2 border-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface))] checked:bg-[rgb(var(--color-primary))] checked:border-[rgb(var(--color-primary))] disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                          />
                        </td>
                        {/* Name — styled as link; row click handles popup */}
                        <td className="px-4 py-3">
                          <div className="text-tok-sm font-medium text-[rgb(var(--color-primary))]">
                            {role.display_name}
                          </div>
                          <div className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                            {role.quota_bearing ? "Quota Bearing" : "Non-Quota"} · {role.direct_indirect}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {roleMotive && <MotionChip label={roleMotive.motion_name ?? role.primary_motion_code} />}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text))]">{role.archetype}</td>
                        <td className="px-4 py-3">
                          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                            {tags.length ? tags.join(", ") : "All"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          {role.is_adopted ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-success))]/10 px-2.5 py-1 text-[10px] font-medium text-[rgb(var(--color-success))]">
                              <CheckCircle className="h-3 w-3" /> Added
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onAdopt(role)}
                              className="rounded-lg border border-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))] hover:text-white transition-colors"
                            >
                              Add to My Roles
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Popup */}
      {popupRole && (
        <RoleLibraryPopup
          role={popupRole}
          motions={motions}
          isAdopted={popupRole.is_adopted}
          onAdopt={() => { setPopupRole(null); onAdopt(popupRole); }}
          onClose={() => setPopupRole(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Roles Tab
// ---------------------------------------------------------------------------
function MyRolesTab({ roles, motions, onRefresh, loading }) {
  const navigate = useNavigate();
  const [search, setSearch]       = useState("");
  const [showAdd, setShowAdd]     = useState(false);
  const [editing, setEditing]     = useState(null); // role_id being edited inline
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);
  // CRL §11.2 — position/plan counts per role, fetched on tab mount
  const [counts, setCounts]       = useState({}); // { [role_id]: { position_count, plan_count } }
  const [countsLoading, setCountsLoading] = useState(false);
  // RoleAssignmentWizard modal state
  const [wizardRole, setWizardRole] = useState(null);

  useEffect(() => {
    if (!roles.length) return;
    setCountsLoading(true);
    // CRL §11.2 — parallel fetch, one call per role, graceful degradation
    Promise.all(
      roles.map(r =>
        fetch(`${API}/roles/${r.role_id}/position-plan-counts`, { credentials: "include" })
          .then(res => res.ok ? res.json() : { role_id: r.role_id, position_count: 0, plan_count: 0 })
          .catch(() => ({ role_id: r.role_id, position_count: 0, plan_count: 0 }))
      )
    ).then(results => {
      setCounts(Object.fromEntries(results.map(r => [r.role_id, r])));
    }).finally(() => setCountsLoading(false));
  }, [roles]);

  const filtered = roles.filter(r =>
    !search ||
    r.role_code.toLowerCase().includes(search.toLowerCase()) ||
    r.role_name.toLowerCase().includes(search.toLowerCase())
  );

  const motionMap = Object.fromEntries(motions.map(m => [m.selling_motion_id, m]));

  const handleSaveEdit = async (role) => {
    setSaving(true);
    try {
      await apiFetch(`/roles/${role.role_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editFields),
      });
      setEditing(null);
      setEditFields({});
      onRefresh();
      setToast({ message: `${role.role_name} updated.`, type: "success" });
    } catch (e) {
      setToast({ message: e.message, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (role) => {
    try {
      await apiFetch(`/roles/${role.role_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !role.is_active }),
      });
      onRefresh();
    } catch (e) {
      setToast({ message: e.message, type: "error" });
    }
  };

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">My Roles</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Manage your tenant's commercial role definitions.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pl-8 pr-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none w-44"
              placeholder="Search roles…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(v => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" /> Add Custom Role
          </button>
        </div>
      </div>

      {showAdd && (
        <AddCustomRoleForm
          onSave={() => { setShowAdd(false); onRefresh(); setToast({ message: "Custom role created.", type: "success" }); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[rgb(var(--color-text-muted))]" />
        </div>
      )}

      {!loading && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden">
          <table className="w-full border-collapse">
            <thead className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
              <tr>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Role Code</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Role Name</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Category</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Archetype</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Positions</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Plans</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Primary Motion</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Status</th>
                <th className="w-64 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                    No roles yet. Add from the Platform Library or create a custom role.
                  </td>
                </tr>
              )}
              {filtered.map(role => {
                const isEditing = editing === role.role_id;
                const motion    = role.sales_motion_id ? motionMap[role.sales_motion_id] : null;
                const locked    = role.is_platform_default;
                const roleCounts = counts[role.role_id];
                return (
                  <tr key={role.role_id} className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {locked && <Lock className="h-3 w-3 text-[rgb(var(--color-text-muted))]" />}
                        <span className="font-mono text-tok-xs text-[rgb(var(--color-text))]">{role.role_code}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <input
                          className="rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1 text-tok-sm text-[rgb(var(--color-text))] w-full focus:outline-none"
                          defaultValue={role.role_name}
                          onChange={e => setEditFields(f => ({ ...f, role_name: e.target.value }))}
                        />
                      ) : (
                        <span className="text-tok-sm text-[rgb(var(--color-text))]">{role.role_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">{role.role_category}</td>
                    <td className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">{role.archetype ?? "—"}</td>
                    {/* CRL §11.1 — position count cell */}
                    <td className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {countsLoading && !roleCounts
                        ? <span className="inline-block h-3 w-8 rounded bg-[rgb(var(--color-surface-muted))] animate-pulse" />
                        : <span>{roleCounts?.position_count ?? 0} pos</span>
                      }
                    </td>
                    {/* CRL §11.1 — plan count cell */}
                    <td className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {countsLoading && !roleCounts
                        ? <span className="inline-block h-3 w-8 rounded bg-[rgb(var(--color-surface-muted))] animate-pulse" />
                        : <span>{roleCounts?.plan_count ?? 0} pln</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      {motion ? <MotionChip label={motion.motion_name} /> : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(role)}
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium ${
                          role.is_active
                            ? "bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]"
                            : "bg-[rgb(var(--color-text-muted))]/10 text-[rgb(var(--color-text-muted))]"
                        }`}
                      >
                        {role.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(role)}
                            disabled={saving}
                            className="rounded-lg bg-[rgb(var(--color-primary))] px-2.5 py-1 text-tok-xs font-medium text-white disabled:opacity-50"
                          >
                            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                          </button>
                          <button type="button" onClick={() => { setEditing(null); setEditFields({}); }} className="rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1.5">
                          {/* CRL §11.3 — Assign to Positions button */}
                          <button
                            type="button"
                            onClick={() => setWizardRole(role)}
                            className="inline-flex items-center gap-1 rounded-lg border border-[rgb(var(--color-primary))] px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))] hover:text-white transition-colors"
                            title="Assign to Positions"
                          >
                            <Users className="h-3 w-3" /> Assign
                          </button>
                          {/* CRL §11.3 — Design Plan button */}
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/compensation-policies?role_id=${role.role_id}&tab=plans`)}
                            className="inline-flex items-center gap-1 rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                            title="Design Plan"
                          >
                            <FileText className="h-3 w-3" /> Plan
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditing(role.role_id); setEditFields({}); }}
                            className="rounded-lg border border-[rgb(var(--color-border))] p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                            title="Edit"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CRL §12 — RoleAssignmentWizard modal */}
      {wizardRole && (
        <RoleAssignmentWizard
          role={wizardRole}
          onClose={() => setWizardRole(null)}
          onSuccess={({ positions_succeeded }) => {
            setWizardRole(null);
            // Refresh counts after successful assignment
            if (positions_succeeded > 0) onRefresh();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selling Motions Tab
// ---------------------------------------------------------------------------
function SellingMotionsTab({ motions, onRefresh, loading }) {
  const [search, setSearch]   = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addFields, setAddFields] = useState({ motion_code: "", motion_name: "", allows_overlay: false });
  const [editing, setEditing] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);

  const filtered = motions.filter(m =>
    !search ||
    m.motion_code.toLowerCase().includes(search.toLowerCase()) ||
    m.motion_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = async () => {
    setSaving(true);
    try {
      await apiFetch("/selling-motions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...addFields, motion_code: addFields.motion_code.toUpperCase().trim() }),
      });
      setShowAdd(false);
      setAddFields({ motion_code: "", motion_name: "", allows_overlay: false });
      onRefresh();
      setToast({ message: "Selling motion added.", type: "success" });
    } catch (e) {
      setToast({ message: e.message, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (motion) => {
    setSaving(true);
    try {
      await apiFetch(`/selling-motions/${motion.selling_motion_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editFields),
      });
      setEditing(null);
      setEditFields({});
      onRefresh();
      setToast({ message: `${motion.motion_name} updated.`, type: "success" });
    } catch (e) {
      setToast({ message: e.message, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Selling Motions</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Manage the selling motions used to classify roles and group coverage.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pl-8 pr-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none w-44"
              placeholder="Search motions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(v => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" /> Add Motion
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 mb-3">
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))] mb-3">New Selling Motion</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Motion Code *</label>
              <input className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
                placeholder="NEW_LOGO"
                value={addFields.motion_code}
                onChange={e => setAddFields(f => ({ ...f, motion_code: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Motion Name *</label>
              <input className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
                placeholder="New Logo / Revenue Growth"
                value={addFields.motion_name}
                onChange={e => setAddFields(f => ({ ...f, motion_name: e.target.value }))}
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded"
                  checked={addFields.allows_overlay}
                  onChange={e => setAddFields(f => ({ ...f, allows_overlay: e.target.checked }))}
                />
                <span className="text-tok-sm text-[rgb(var(--color-text))]">Allows Overlay</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))]">Cancel</button>
            <button type="button" onClick={handleAdd} disabled={saving || !addFields.motion_code.trim() || !addFields.motion_name.trim()}
              className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Motion"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[rgb(var(--color-text-muted))]" /></div>
      ) : (
        <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden">
          <table className="w-full border-collapse">
            <thead className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
              <tr>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Code</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Name</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Overlay</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Credit Split</th>
                <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Status</th>
                <th className="w-20 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.selling_motion_id} className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]/40">
                  <td className="px-4 py-3 font-mono text-tok-xs text-[rgb(var(--color-text))]">{m.motion_code}</td>
                  <td className="px-4 py-3">
                    {editing === m.selling_motion_id ? (
                      <input className="rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
                        defaultValue={m.motion_name}
                        onChange={e => setEditFields(f => ({ ...f, motion_name: e.target.value }))}
                      />
                    ) : (
                      <span className="text-tok-sm text-[rgb(var(--color-text))]">{m.motion_name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">{m.allows_overlay ? "Yes" : "No"}</td>
                  <td className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">{m.credit_split_pct != null ? `${(m.credit_split_pct * 100).toFixed(0)}%` : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-medium ${m.is_active ? "bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]" : "bg-[rgb(var(--color-text-muted))]/10 text-[rgb(var(--color-text-muted))]"}`}>
                      {m.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editing === m.selling_motion_id ? (
                      <div className="flex items-center justify-end gap-1.5">
                        <button type="button" onClick={() => handleSaveEdit(m)} disabled={saving}
                          className="rounded-lg bg-[rgb(var(--color-primary))] px-2.5 py-1 text-tok-xs font-medium text-white disabled:opacity-50">
                          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                        </button>
                        <button type="button" onClick={() => { setEditing(null); setEditFields({}); }}
                          className="rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => { setEditing(m.selling_motion_id); setEditFields({}); }}
                        className="rounded-lg border border-[rgb(var(--color-border))] p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------
const TABS = [
  { key: "library",  label: "Platform Library" },
  { key: "my_roles", label: "My Roles" },
  { key: "motions",  label: "Selling Motions" },
];

export default function CommercialRoleLibrary() {
  const [tab, setTab]               = useState("library");
  const [libraryRoles, setLibraryRoles] = useState([]);
  const [myRoles, setMyRoles]       = useState([]);
  const [motions, setMotions]       = useState([]);
  const [loadingLib, setLoadingLib] = useState(true);
  const [loadingMy,  setLoadingMy]  = useState(true);
  const [loadingMot, setLoadingMot] = useState(true);
  const [adoptTarget, setAdoptTarget] = useState(null); // ref_role_library row for modal
  const [toast, setToast]           = useState(null);

  const fetchLibrary = useCallback(async () => {
    setLoadingLib(true);
    try {
      const data = await apiFetch("/role-library");
      setLibraryRoles(data.data ?? data);
    } catch { /* silent — surface is non-critical */ }
    finally { setLoadingLib(false); }
  }, []);

  const fetchMyRoles = useCallback(async () => {
    setLoadingMy(true);
    try {
      const data = await apiFetch("/roles");
      setMyRoles(data.data ?? []);
    } catch { }
    finally { setLoadingMy(false); }
  }, []);

  const fetchMotions = useCallback(async () => {
    setLoadingMot(true);
    try {
      const data = await apiFetch("/selling-motions");
      setMotions(data.data ?? data.selling_motions ?? []);
    } catch { }
    finally { setLoadingMot(false); }
  }, []);

  useEffect(() => {
    fetchLibrary();
    fetchMyRoles();
    fetchMotions();
  }, [fetchLibrary, fetchMyRoles, fetchMotions]);

  const handleAdoptSuccess = () => {
    setAdoptTarget(null);
    fetchLibrary();
    fetchMyRoles();
  };

  return (
    <div className="p-6">
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <BookOpen className="h-5 w-5 text-[rgb(var(--color-primary))]" />
          <h1 className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">Commercial Role Library</h1>
        </div>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          Define and manage reusable commercial role definitions that drive compensation plan eligibility, quota assignment, and credit attribution logic.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-[rgb(var(--color-border))] mb-6">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-tok-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]"
                : "border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "library" && (
        <PlatformLibraryTab
          libraryRoles={libraryRoles}
          motions={motions}
          onAdopt={setAdoptTarget}
          loading={loadingLib}
        />
      )}
      {tab === "my_roles" && (
        <MyRolesTab
          roles={myRoles}
          motions={motions}
          onRefresh={fetchMyRoles}
          loading={loadingMy}
        />
      )}
      {tab === "motions" && (
        <SellingMotionsTab
          motions={motions}
          onRefresh={fetchMotions}
          loading={loadingMot}
        />
      )}

      {/* Adoption modal */}
      {adoptTarget && (
        <RoleAdoptionModal
          role={adoptTarget}
          onSuccess={handleAdoptSuccess}
          onClose={() => setAdoptTarget(null)}
        />
      )}
    </div>
  );
}
